import { useState, FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Printer, Copy } from 'lucide-react';
import ClearPathLogo from '../components/ClearPathLogo';
import { fmt, fmtCents, fmtChange, fmtCount, fmtPct } from '../utils/formatters';

/**
 * Public sample audit — /audit. Drop a month of item sales, get the one-page
 * money audit back (spec docs/superpowers/specs/2026-10-28-sample-audit-design.md).
 * No account, nothing stored, no model: the words are fixed per detector on the
 * server and every number is formatted here through formatters.ts.
 */

type Finding = {
  detector: string; title: string; impact_low: number; impact_high: number; confidence: number;
  basis: string; facts: Record<string, any>; inputs: Record<string, any>; how: string; try: string;
};
export type AuditResult = {
  rows_read: number; rows_excluded: number; rows_skipped: number;
  date_range: [string | null, string | null]; days: number;
  mapping: { date: string | null; item: string | null; amount: string | null; qty: string | null; category: string | null };
  fits_item_sales: boolean; enough_history: boolean; item_asked: string | null; item_found: boolean;
  finding: Finding | null; held_back: number; not_looked_at: string[];
  timemachine: { total_found_low: number; total_found_high: number; start_date: string; end_date: string; weeks_analyzed: number } | null;
};

const EXPORTS = [
  { pos: 'Square', steps: 'Reports, then Item Sales. Set the dates to the last full month. Export, then Items Detail CSV.' },
  { pos: 'Toast', steps: 'Reports, then Menu, then Item Details (Item Selection Details). Pick the month. Export CSV.' },
  { pos: 'Clover', steps: 'Reporting, then Items. Pick the month. Export at the top right.' },
  { pos: 'Shopify', steps: 'Orders, then Export. Choose the month and "Plain CSV file". Line items are included.' },
];

const SHARE_KEYS = /share|rate|pct|percent|capture|confidence/i;
const RATIO_KEYS = /ratio|multiplier/i;
const MONEY_KEYS = /price|rev|dollar|amount|cost|margin|impact|spend|_usd/i;

/** A raw detector input as text, through formatters only. Shares before money,
 *  because "rev_share" is a share; a ratio reads as the change it is (1.54 -> +53.9%). */
export function formatInput(key: string, value: any): string {
  if (typeof value !== 'number') return String(value ?? '');
  if (SHARE_KEYS.test(key)) return fmtPct(value <= 1 ? value * 100 : value);
  if (RATIO_KEYS.test(key)) return fmtChange((value - 1) * 100);
  if (MONEY_KEYS.test(key)) return Number.isInteger(value) || Math.abs(value) >= 100 ? fmt(value) : fmtCents(value);
  return fmtCount(value);
}

const label = (key: string) => key.replace(/_/g, ' ').replace(/\b(\d+)d\b/g, 'last $1 days');

export function dayWords(iso: string | null, withYear = false): string {
  if (!iso) return '';
  return new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}),
  });
}
const rangeWords = (r: [string | null, string | null]) => `${dayWords(r[0])} to ${dayWords(r[1], true)}`;
const monthRange = (f: Finding) => `${fmt(f.impact_low)}–${fmt(f.impact_high)} a month`;

/** The page as plain text, for a reply Lucas pastes. */
export function auditAsText(r: AuditResult, shop: string): string {
  const name = shop.trim() || 'Your shop';
  const lines = [`${name}: one thing in your numbers`, `From your register export, ${rangeWords(r.date_range)}. Nothing else was used, and nothing was kept.`, ''];
  if (r.finding) {
    const f = r.finding;
    lines.push('The finding', f.title, `${monthRange(f)}, ${f.basis}.`, f.facts.detail ? String(f.facts.detail) : '', '');
    lines.push('How we got there', f.how, '');
    lines.push('One thing to try this week', f.try, '');
  } else {
    lines.push(r.enough_history ? 'Nothing in this file cleared the bar.' : `The detectors need four weeks. This file holds ${fmtCount(r.days)} days.`, '');
  }
  lines.push(`Not looked at: ${r.not_looked_at.join(', ')}.`);
  lines.push(`Check it yourself: ${fmtCount(r.rows_read)} rows read; the date from "${r.mapping.date}", the item from "${r.mapping.item}", the amount from "${r.mapping.amount}".`);
  if (r.timemachine) lines.push(`Had ClearPath been watching since ${dayWords(r.timemachine.start_date, true)}, it would have found ${fmt(r.timemachine.total_found_low)} to ${fmt(r.timemachine.total_found_high)}.`);
  lines.push('', 'Lucas Donaldson · clearpathsmb.com');
  return lines.filter((l, i, a) => !(l === '' && a[i - 1] === '')).join('\n');
}

const Eyebrow = ({ children }: { children: string }) => (
  <div className="font-sans text-xs font-medium uppercase tracking-wide text-[#4A2D5E] mb-1.5">{children}</div>
);
const Section = ({ eyebrow, children }: { eyebrow: string; children: React.ReactNode }) => (
  <section className="py-5 border-t border-[#E6E2DE] first:border-0 first:pt-0">
    <Eyebrow>{eyebrow}</Eyebrow>
    {children}
  </section>
);

export function AuditPage({ result: r, shop }: { result: AuditResult; shop: string }) {
  const f = r.finding;
  const name = shop.trim() || 'Your shop';
  return (
    <article className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 sm:p-8 shadow-card print:shadow-none print:border-0 print:p-0" aria-label="Your one-page audit">
      <header className="mb-5">
        <h2 className="font-serif text-2xl text-[#2D2A2E] leading-snug">{name}: one thing in your numbers</h2>
        <p className="font-sans text-sm text-[#6B6670] mt-1">
          From your register export, {rangeWords(r.date_range)}. Nothing else was used, and nothing was kept.
        </p>
      </header>

      {f ? (
        <>
          <Section eyebrow="The finding">
            {r.item_asked && (
              <p className="font-sans text-xs text-[#6B6670] mb-2">
                {r.item_found
                  ? `This is the item from the email, ${r.item_asked}.`
                  : `${r.item_asked} did not clear the bar in this file. Here is what did.`}
              </p>
            )}
            <p className="font-serif text-lg text-[#2D2A2E] leading-snug">{f.title}</p>
            <p className="font-sans text-2xl font-bold text-[#2D2A2E] tabular-nums mt-2">{monthRange(f)}</p>
            <p className="font-sans text-xs text-[#6B6670]">{f.basis}</p>
            {f.facts.detail && <p className="font-sans text-sm text-[#2D2A2E] leading-relaxed mt-2">{String(f.facts.detail)}</p>}
          </Section>

          <Section eyebrow="How we got there">
            <p className="font-sans text-sm text-[#2D2A2E] leading-relaxed">{f.how}</p>
            <table className="mt-3 w-full text-sm font-sans">
              <caption className="sr-only">The numbers used</caption>
              <tbody>
                {Object.entries(f.inputs).filter(([, v]) => typeof v === 'number' || typeof v === 'string').map(([k, v]) => (
                  <tr key={k} className="border-t border-[#E6E2DE]">
                    <th scope="row" className="py-1.5 pr-3 text-left font-normal text-[#6B6670]">{label(k)}</th>
                    <td className="py-1.5 text-right tabular-nums text-[#2D2A2E]">{formatInput(k, v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section eyebrow="One thing to try this week">
            <p className="font-sans text-sm text-[#2D2A2E] leading-relaxed">{f.try}</p>
          </Section>
        </>
      ) : (
        <Section eyebrow="The finding">
          {!r.fits_item_sales ? (
            <p className="font-sans text-sm text-[#2D2A2E] leading-relaxed">This looks like a payments or order list, not item sales. The check needs each item on its own line. The export steps above give the right report.</p>
          ) : !r.enough_history ? (
            <p className="font-sans text-sm text-[#2D2A2E] leading-relaxed">The detectors need four weeks of sales to say anything worth money. This file holds {fmtCount(r.days)} days, {rangeWords(r.date_range)}. Export a full month and run it again.</p>
          ) : (
            <p className="font-sans text-sm text-[#2D2A2E] leading-relaxed">Nothing in this file cleared the bar, {rangeWords(r.date_range)}. That is a real answer: no item is running out early, fading, or selling in the wrong hour by enough to be worth your week.</p>
          )}
        </Section>
      )}

      <Section eyebrow="What was not looked at">
        <p className="font-sans text-sm text-[#2D2A2E] leading-relaxed">
          {r.not_looked_at.join(', ')}. The finding stands on sales alone; those would change the dollars, not the pattern.
        </p>
      </Section>

      <Section eyebrow="Check it yourself">
        <p className="font-sans text-sm text-[#2D2A2E] leading-relaxed">
          {fmtCount(r.rows_read)} rows read{r.rows_excluded > 0 ? `, ${fmtCount(r.rows_excluded)} refunded or voided rows left out` : ''}{r.rows_skipped > 0 ? `, ${fmtCount(r.rows_skipped)} rows with no readable date skipped` : ''}.
          The date came from “{r.mapping.date}”, the item from “{r.mapping.item}”, the amount from “{r.mapping.amount}”. A pivot on those three columns gives the same numbers.
        </p>
        {r.held_back > 0 && (
          <p className="font-sans text-sm text-[#6B6670] mt-2">
            {r.held_back === 1 ? 'One more thing cleared the bar' : `${r.held_back === 2 ? 'Two' : fmtCount(r.held_back)} more things cleared the bar`}. That is the next conversation.
          </p>
        )}
        {r.timemachine && (
          <p className="font-sans text-sm text-[#2D2A2E] mt-2">
            Had ClearPath been watching since {dayWords(r.timemachine.start_date, true)}, it would have found you {fmt(r.timemachine.total_found_low)} to {fmt(r.timemachine.total_found_high)}, counted the cautious way.
          </p>
        )}
      </Section>

      <footer className="pt-4 border-t border-[#E6E2DE] font-sans text-[11px] text-[#6B6670]">
        Nothing from your file was kept. Every number above is computed from its rows; no model wrote any of it.
      </footer>
    </article>
  );
}

export default function SampleAuditPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [item, setItem] = useState(params.get('item') || '');
  const [shop, setShop] = useState(params.get('shop') || '');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<AuditResult | null>(null);
  const [copied, setCopied] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!file) { setError('Choose your export first.'); return; }
    setBusy(true); setError(''); setResult(null);
    const body = new FormData();
    body.append('file', file);
    body.append('item', item);
    try {
      const res = await axios.post('/api/audit/sample', body, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'That file could not be read. Export it again as CSV and try once more.');
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!result) return;
    try { await navigator.clipboard.writeText(auditAsText(result, shop)); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* clipboard unavailable */ }
  };

  const input = 'w-full h-11 bg-white border border-[#E6E2DE] rounded-[8px] px-3 font-sans text-sm text-[#2D2A2E] focus:outline-none focus:border-[#4A2D5E] focus:ring-2 focus:ring-[#4A2D5E]/20';

  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      <div className="bg-[#4A2D5E] px-6 py-4 flex items-center justify-between print:hidden">
        <ClearPathLogo variant="white" size="sm" />
        <div className="text-right">
          <div className="font-serif text-white text-sm">The sales check</div>
          <div className="font-sans text-white/60 text-xs">One page, no account</div>
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-6 py-10">
        <div className="mb-8 text-center print:hidden">
          <h1 className="font-serif text-3xl text-[#2D2A2E] leading-snug">One thing in your register’s numbers</h1>
          <p className="font-sans text-sm text-[#6B6670] mt-2 max-w-md mx-auto leading-relaxed">
            Drop a month of item sales. One page comes back in under a minute. Nothing is stored.
          </p>
        </div>

        {!result && (
          <>
            <details className="bg-white border border-[#E6E2DE] rounded-[14px] px-5 py-4 mb-4 shadow-card">
              <summary className="font-serif text-base text-[#2D2A2E] cursor-pointer">How to export a month from your register</summary>
              <dl className="mt-3 space-y-2">
                {EXPORTS.map((e) => (
                  <div key={e.pos} className="grid grid-cols-[5rem_1fr] gap-2">
                    <dt className="font-sans text-sm font-medium text-[#2D2A2E]">{e.pos}</dt>
                    <dd className="font-sans text-sm text-[#6B6670] leading-relaxed">{e.steps}</dd>
                  </div>
                ))}
              </dl>
            </details>

            <form onSubmit={submit} className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card space-y-4" aria-busy={busy}>
              <div>
                <label htmlFor="audit-file" className="block font-sans text-xs font-medium text-[#6B6670] mb-1.5">Your export (CSV)</label>
                <input id="audit-file" type="file" accept=".csv,text/csv" className="block w-full font-sans text-sm text-[#2D2A2E] file:mr-3 file:h-11 file:px-4 file:rounded-[8px] file:border file:border-[#E6E2DE] file:bg-white file:font-sans file:text-sm file:text-[#4A2D5E]"
                  onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="audit-shop" className="block font-sans text-xs font-medium text-[#6B6670] mb-1.5">Shop name (optional, stays on this page)</label>
                  <input id="audit-shop" name="shop" value={shop} onChange={(e) => setShop(e.target.value)} className={input} maxLength={80} />
                </div>
                <div>
                  <label htmlFor="audit-item" className="block font-sans text-xs font-medium text-[#6B6670] mb-1.5">Item from the email (optional)</label>
                  <input id="audit-item" name="item" value={item} onChange={(e) => setItem(e.target.value)} className={input} maxLength={80} placeholder="Butter croissant" />
                </div>
              </div>
              {error && <p role="alert" className="font-sans text-sm text-[#B54A4A]">{error}</p>}
              <button type="submit" disabled={busy}
                className="inline-flex items-center gap-2 h-11 bg-[#4A2D5E] text-white hover:bg-[#3D254D] disabled:opacity-60 rounded-[8px] font-sans font-medium px-6 text-sm transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-[#4A2D5E]/20">
                {busy ? 'Reading your file' : 'Run the check'}
                {!busy && <ArrowRight size={15} strokeWidth={2} aria-hidden="true" />}
              </button>
              <p className="font-sans text-[11px] text-[#6B6670]">The file is read once and never written anywhere. No account, no email asked.</p>
            </form>
          </>
        )}

        {result && (
          <>
            <AuditPage result={result} shop={shop} />
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3 print:hidden">
              <button onClick={() => window.print()} className="inline-flex items-center gap-2 h-11 bg-white border border-[#E6E2DE] text-[#2D2A2E] hover:border-[#4A2D5E] rounded-[8px] font-sans font-medium px-5 text-sm transition-all">
                <Printer size={15} strokeWidth={2} aria-hidden="true" /> Print this page
              </button>
              <button onClick={copy} className="inline-flex items-center gap-2 h-11 bg-white border border-[#E6E2DE] text-[#2D2A2E] hover:border-[#4A2D5E] rounded-[8px] font-sans font-medium px-5 text-sm transition-all">
                <Copy size={15} strokeWidth={2} aria-hidden="true" /> {copied ? 'Copied' : 'Copy as text'}
              </button>
              <button onClick={() => navigate('/auth')} className="inline-flex items-center gap-2 h-11 bg-[#4A2D5E] text-white hover:bg-[#3D254D] rounded-[8px] font-sans font-medium px-5 text-sm transition-all shadow-sm">
                Run it on your register every morning <ArrowRight size={15} strokeWidth={2} aria-hidden="true" />
              </button>
            </div>
            <button onClick={() => { setResult(null); setFile(null); }} className="block mx-auto mt-4 font-sans text-sm text-[#6B6670] hover:text-[#4A2D5E] print:hidden">Run another file</button>
          </>
        )}

        <p className="font-sans text-xs text-[#6B6670] text-center mt-10 print:hidden">
          Prepared by <span className="font-medium text-[#4A2D5E]">ClearPath</span> · Clear Path SMB LLC · Nothing is stored
        </p>
      </main>
    </div>
  );
}
